import type { Card, Reminder } from './types.js';

export const HOUR_MS = 3_600_000;
export const DAY_MS = 24 * HOUR_MS;

/** The offsets the sheet offers for reminders relative to the due time. */
export const REMINDER_PRESETS = [
  { key: 'due', ms: 0 },
  { key: '1h', ms: HOUR_MS },
  { key: '1d', ms: DAY_MS },
  { key: '1w', ms: 7 * DAY_MS },
] as const;

export type ReminderPresetKey = (typeof REMINDER_PRESETS)[number]['key'];

/**
 * When a card's reminders ring, sorted and without duplicates. A relative reminder needs the
 * due time and is silent without one.
 */
export function reminderTimes(card: Pick<Card, 'reminders' | 'dueAt'>): number[] {
  return reminderAlerts(card).map((a) => a.at);
}

export interface ReminderAlert {
  at: number;
  /** An alarm rather than a notification; two reminders at the same time ring if either does. */
  ring: boolean;
}

/** The same times with whether each one rings. */
export function reminderAlerts(card: Pick<Card, 'reminders' | 'dueAt'>): ReminderAlert[] {
  const byTime = new Map<number, boolean>();
  for (const r of card.reminders) {
    const at = r.kind === 'at' ? r.at : card.dueAt === null ? null : card.dueAt - r.ms;
    if (at === null) continue;
    byTime.set(at, (byTime.get(at) ?? false) || r.ring === true);
  }
  return [...byTime].sort((a, b) => a[0] - b[0]).map(([at, ring]) => ({ at, ring }));
}

export function sameReminder(a: Reminder, b: Reminder): boolean {
  return a.kind === b.kind && (a.kind === 'at' ? a.at === (b as { at: number }).at : a.ms === (b as { ms: number }).ms);
}
